import { FC, ReactNode, useState } from 'react';
import { Box, Container, Grid, Theme, Typography, useMediaQuery } from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';

interface TrustProgramProps {
  titleOrange: string;
  titleBlack: string;
  programList: Array<{ description: string; title: string; svgIcon: ReactNode; svgIconOnHover: ReactNode }>;
  id?: string;
}

const useStyles = makeStyles((theme: Theme) => {
  return createStyles({
    gridContainer: {
      marginBottom: theme.spacing(8),
      marginTop: theme.spacing(22),
      [theme.breakpoints.down('md')]: {
        marginTop: theme.spacing(13.3),
      },
    },
    titleOrange: {
      color: 'rgba(241, 48, 5, 1)',
      fontWeight: 900,
      fontSize: 102,
      textAlign: 'center',
      paddingRight: theme.spacing(5),
      [theme.breakpoints.down('md')]: {
        fontSize: 72,
        lineHeight: '72px',
        paddingRight: theme.spacing(2),
      },
      [theme.breakpoints.down('xs')]: {
        paddingRight: 0,
      },
    },
    titleBlack: {
      color: 'rgba(69, 55, 55, 1)',
      fontWeight: 900,
      fontSize: 102,
      textAlign: 'center',
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
        fontSize: 72,
        lineHeight: '72px',
        marginBottom: theme.spacing(8),
      },
    },
    container: {
      '&:hover': {
        cursor: 'pointer',
        '& div': {
          '& p': {
            fontWeight: 600,
          },
          '& .MuiBox-root': {
            width: '100%',
          },
        },
      },
      [theme.breakpoints.down('xs')]: {
        marginBottom: theme.spacing(3),
      },
    },
    title: {
      color: 'rgba(69, 55, 55, 1)',
      fontWeight: 400,
      fontSize: 48,
      marginLeft: theme.spacing(-3.3),
      marginTop: theme.spacing(9.1),
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: 30,
        marginTop: 0,
      },
    },
    description: {
      color: 'rgba(79, 82, 96, 1)',
      fontWeight: 400,
      fontSize: 24,
      [theme.breakpoints.down('sm')]: {
        maxWidth: 500,
        fontSize: 16,
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: 227,
      },
    },
    blockWitIcon: {
      padding: theme.spacing(4, 0, 2),
    },
    centeredGrid: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'flex-end',
      position: 'relative',
      marginLeft: theme.spacing(-3.3),
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
    },
    divider: {
      width: 264,
      height: 2,
      backgroundColor: 'rgba(69, 55, 55, 1)',
      marginLeft: theme.spacing(-3.3),
      transition: 'width 0.5s ease',
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
      [theme.breakpoints.down('sm')]: {
        width: 80,
        marginBottom: theme.spacing(1.3),
      },
    },
    textBlock: {
      marginTop: theme.spacing(-7.6),
      marginBottom: theme.spacing(8.1),
    },
    titleOnTouch: {
      color: 'rgba(69, 55, 55, 1)',
      fontWeight: 600,
      fontSize: 48,
      marginLeft: theme.spacing(-3.3),
      marginTop: theme.spacing(9.1),
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
      [theme.breakpoints.down('sm')]: {
        fontSize: 30,
        marginTop: 0,
      },
    },
    dividerOnTouch: {
      width: '100%',
      height: 2,
      backgroundColor: 'rgba(69, 55, 55, 1)',
      marginLeft: theme.spacing(-3.3),
      transition: 'width 0.5s ease',
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
      [theme.breakpoints.down('sm')]: {
        marginBottom: theme.spacing(1.3),
      },
    },
    descriptionOnTouch: {
      color: 'rgba(79, 82, 96, 1)',
      fontWeight: 600,
      fontSize: 24,
      [theme.breakpoints.down('sm')]: {
        maxWidth: 500,
        fontSize: 16,
      },
      [theme.breakpoints.down('xs')]: {
        maxWidth: 227,
      },
    },
  });
});

const TrustProgram: FC<TrustProgramProps> = (props) => {
  const { titleOrange, id, programList, titleBlack } = props;
  const classes = useStyles();
  const [hover, setHover] = useState<{ [key: string]: boolean }>({});
  const isSmall = useMediaQuery((theme: Theme) => theme.breakpoints.up('sm'));
  const isMedium = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

  const handleMouseOver = (key: string) => {
    setHover((prev) => ({ ...prev, [key]: true }));
  };

  const handleMouseOut = (key: string) => {
    setHover((prev) => ({ ...prev, [key]: false }));
  };

  const handleTouchStart = (key: string) => {
    // Reset all hover states
    const resetHover = Object.keys(hover).reduce((acc, curr) => {
      acc[curr] = false;
      return acc;
    }, {});

    // Toggle hover state for the touched element
    setHover((prev) => ({
      ...resetHover,
      [key]: !prev[key],
    }));
  };

  return (
    <Container data-aos="fade-up" data-aos-delay="400" fixed id={id}>
      {!isSmall ? (
        <Grid container justifyContent="center" className={classes.gridContainer}>
          <Grid sm={12} md={6} item>
            <Typography className={classes.titleOrange}>{titleOrange}</Typography>
          </Grid>
          <Grid sm={12} md={6} item>
            <Typography className={classes.titleBlack}>{titleBlack}</Typography>
          </Grid>
        </Grid>
      ) : (
        <Box display={'flex'} justifyContent={'center'} mt={22} mb={12}>
          <Typography className={classes.titleOrange}>{titleOrange}</Typography>
          <Typography className={classes.titleBlack}>{titleBlack}</Typography>
        </Box>
      )}
      {programList.map((program) => {
        return isMedium ? (
          <Grid
            container
            key={program.title}
            className={classes.container}
            onMouseOver={() => handleMouseOver(program.title)}
            onMouseOut={() => handleMouseOut(program.title)}
            onTouchStart={() => handleTouchStart(program.title)}
            spacing={6}
          >
            <Grid sm={7} item>
              <Grid container>
                <Grid sm={2} className={classes.blockWitIcon} item>
                  <div>{hover[program.title] ? program.svgIconOnHover : program.svgIcon}</div>
                </Grid>
                <Grid sm={10} item>
                  <Typography className={hover[program.title] ? classes.titleOnTouch : classes.title}>{program.title}</Typography>
                  <Box className={hover[program.title] ? classes.dividerOnTouch : classes.divider} />
                </Grid>
              </Grid>
            </Grid>
            <Grid sm={5} className={classes.centeredGrid} item>
              <Typography className={hover[program.title] ? classes.descriptionOnTouch : classes.description}>
                {program.description}
              </Typography>
            </Grid>
          </Grid>
        ) : (
          <Grid
            container
            key={program.title}
            className={classes.container}
            onTouchStart={() => handleTouchStart(program.title)}
            spacing={isSmall ? 6 : 2}
          >
            <Grid sm={2} item>
              <Box>{hover[program.title] ? program.svgIconOnHover : program.svgIcon}</Box>
            </Grid>
            <Grid sm={10} className={classes.textBlock} item>
              <Typography className={hover[program.title] ? classes.titleOnTouch : classes.title}>{program.title}</Typography>
              <Box className={hover[program.title] ? classes.dividerOnTouch : classes.divider} />
              <Typography className={hover[program.title] ? classes.descriptionOnTouch : classes.description}>
                {program.description}
              </Typography>
            </Grid>
          </Grid>
        );
      })}
    </Container>
  );
};

export default TrustProgram;
